/**
 * One pie of requirement status, drawn as a ring: its name above it, the
 * group's total in the middle, and a 'STATUS n' label beside each slice.
 *
 * Hand-written SVG (no chart library). The slices are the same shapes the
 * exported report page draws - see pieGeometry.ts - but the figure is not the
 * page's: the page keeps a filled pie with its counts beneath, and this is the
 * app's own view of the same numbers.
 *
 * NUMBERS CAN BE SWITCHED OFF (`numbers`), leaving the ring and its name for a
 * slide. With them on, a number is never left to be read off an angle: every
 * slice says its count. A status with no requirements has no slice and so no
 * label - its zero is said by its absence.
 *
 * Slice colours come from the report's own style list (meta.status_styles) and
 * are never written here: `fill` on a light theme and `fill_dark` on a dark one,
 * the same hue stepped for the surface, as the exported page does. The divider
 * between slices and the text take the MUI theme's colours.
 *
 * THE HOLE IS CUT, NOT PAINTED. A card in the app is not a flat colour in the
 * dark theme (MUI lightens a raised surface), so a disc painted over the middle
 * would show as a darker circle; a mask removes the middle instead and whatever
 * is behind the card shows through.
 *
 * Numbers and statuses only, because that is all it is given: there is nowhere
 * to pass a requirement id or text.
 */

import { useId } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import type { StatusStyle } from '../../schema'
import {
  PIE_BOX,
  PIE_HOLE,
  PIE_RADIUS,
  layoutPie,
  pieDescription,
  pieTotal,
  rim,
  sliceShape,
  statusWord,
  styleFor,
} from './pieGeometry'
import type { PiePart, Wedge } from './pieGeometry'

export interface StatusPieProps {
  /** The group's name: 'All requirements', 'L3', a subsystem. */
  label: string
  /** Each status and its count, in the report's status order. */
  parts: PiePart[]
  styles: StatusStyle[]
  /** A larger pie, for the whole program. */
  large?: boolean
  /**
   * Whether the figure states its numbers: the total in the middle and a
   * 'STATUS n' label beside each slice. Off is the picture and its name only,
   * for a slide; the numbers stay in the accessible name and the hover titles.
   */
  numbers?: boolean
}

export default function StatusPie({
  label,
  parts,
  styles,
  large = false,
  numbers = true,
}: StatusPieProps) {
  const theme = useTheme()
  // useId's colons are legal in an id but awkward in a url() reference.
  const maskId = `pie-hole-${useId().replace(/:/g, '')}`
  if (pieTotal(parts) === 0) return null // a group with no requirements has no pie

  const description = pieDescription(label, parts)
  const dark = theme.palette.mode === 'dark'
  const paper = theme.palette.background.paper
  const centre = PIE_BOX / 2

  // Room around the ring for a label beside each slice. The drawing is scaled
  // to the figure's width, so a label's size in drawing units is worked back
  // from the size it should be on screen.
  // Without numbers there are no labels to make room for, and the figure is
  // the ring at the size it has inside the labelled one.
  const padX = numbers ? LABEL_PAD_X : 0
  const padY = numbers ? LABEL_PAD_Y : 0
  const boxWidth = PIE_BOX + 2 * padX
  const boxHeight = PIE_BOX + 2 * padY
  const labelled = large ? 330 : 216
  const width = numbers ? labelled : (labelled * PIE_BOX) / (PIE_BOX + 2 * LABEL_PAD_X)
  const scale = width / boxWidth
  const labelSize = 11.5 / scale
  const wedges = layoutPie(parts)
  const labels = numbers ? sliceLabels(wedges, labelSize * 1.15) : []

  return (
    <Box component="figure" sx={{ m: 0, textAlign: 'center', width }}>
      <Typography variant="subtitle2" component="figcaption" sx={{ overflowWrap: 'anywhere' }}>
        {label}
      </Typography>
      <svg
        viewBox={`${-padX} ${-padY} ${boxWidth} ${boxHeight}`}
        role="img"
        aria-label={description}
        style={{ display: 'block', width, height: width * (boxHeight / boxWidth), margin: '0 auto' }}
      >
        <title>{description}</title>
        <defs>
          <mask id={maskId}>
            <rect x={0} y={0} width={PIE_BOX} height={PIE_BOX} fill="white" />
            <circle cx={centre} cy={centre} r={PIE_HOLE} fill="black" />
          </mask>
        </defs>
        <g mask={`url(#${maskId})`}>
          {wedges.map((wedge) => {
            const shape = sliceShape(wedge)
            const style = styleFor(styles, wedge.status)
            const common = {
              fill: (dark ? style?.fill_dark : style?.fill) ?? 'currentColor',
              // The divider is the page background so slices read as separated.
              stroke: paper,
              strokeWidth: 0.75,
              strokeLinejoin: 'round' as const,
            }
            const tip = <title>{`${statusWord(wedge.status)}: ${wedge.count}`}</title>
            return shape.kind === 'circle' ? (
              <circle key={wedge.status} cx={shape.cx} cy={shape.cy} r={shape.r} {...common}>
                {tip}
              </circle>
            ) : (
              <path key={wedge.status} d={shape.d} {...common}>
                {tip}
              </path>
            )
          })}
        </g>
        {/* The total, in the hole the mask cut. */}
        {numbers && (
        <>
        <text
          x={centre}
          y={centre - 3}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={18}
          fontWeight={600}
          fill={theme.palette.text.primary}
        >
          {pieTotal(parts)}
        </text>
        <text
          x={centre}
          y={centre + 12}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={8.5}
          fill={theme.palette.text.secondary}
        >
          total
        </text>
        </>
        )}
        {/* One label beside each slice: the status and its count. */}
        {labels.map((item) => (
          <g key={item.status} data-slice-label={item.status}>
            <line
              x1={item.fromX}
              y1={item.fromY}
              x2={item.elbowX}
              y2={item.y}
              stroke={theme.palette.text.secondary}
              strokeWidth={0.5}
            />
            <text
              x={item.x}
              y={item.y}
              textAnchor={item.right ? 'start' : 'end'}
              dominantBaseline="central"
              fontSize={labelSize}
              fill={theme.palette.text.primary}
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {`${statusWord(item.status)} ${item.count}`}
            </text>
          </g>
        ))}
      </svg>
    </Box>
  )
}

/** Space beside and above the ring, in drawing units, for the slice labels. */
const LABEL_PAD_X = 40
const LABEL_PAD_Y = 10

interface SliceLabel {
  status: string
  count: number
  /** True when the label sits to the right of the ring. */
  right: boolean
  /** Where the leader leaves the rim. */
  fromX: number
  fromY: number
  /** Where the leader meets the text. */
  elbowX: number
  x: number
  y: number
}

/**
 * Where each slice's label goes: just outside the rim at the middle of the
 * slice, to the right of the ring for the right half and to the left for the
 * left. Labels on one side that would overlap are pushed apart downwards.
 */
function sliceLabels(wedges: Wedge[], spacing: number): SliceLabel[] {
  const centre = PIE_BOX / 2
  const labels = wedges.map((wedge) => {
    // A whole ring has no middle to speak of: label it at 3 o'clock.
    const middle = wedge.sweep >= 360 ? 90 : (wedge.start + wedge.end) / 2
    const radians = (middle * Math.PI) / 180
    const right = Math.sin(radians) >= 0
    const from = rim(middle)
    const reach = PIE_RADIUS + 5
    const elbowX = centre + reach * Math.sin(radians)
    return {
      status: wedge.status,
      count: wedge.count,
      right,
      fromX: from.x,
      fromY: from.y,
      elbowX,
      x: elbowX + (right ? 1.5 : -1.5),
      y: centre - reach * Math.cos(radians),
    }
  })
  for (const side of [true, false]) {
    const column = labels.filter((item) => item.right === side).sort((a, b) => a.y - b.y)
    for (let index = 1; index < column.length; index += 1) {
      column[index].y = Math.max(column[index].y, column[index - 1].y + spacing)
    }
  }
  return labels
}
